import http from "./httpService.ts";
import {SUPPORTED_FILE_TYPES} from "../models/constants/constants.ts";
import mime from "mime-types";
import {IProjectStreamLinks} from "../models/interfaces/projectStreamLinks.interface.ts";
import {INewProject} from "../models/interfaces/newProject.interface.ts";
import {IProjectStatus} from "../models/interfaces/projectStatus.interface.ts";
import {IUploadLink} from "../models/interfaces/uploadLink.interface.ts";
import {IFeedbackForm} from "../models/interfaces/feedbackForm.interface.ts";
import {IProjectDto} from "../models/interfaces/dtos/project.dto.interface.ts";
import {SearchSeriesResultDto} from "../models/interfaces/dtos/searchSeriesResult.dto.interface.ts";
import {SupportedLanguagesDto} from "../models/interfaces/dtos/supportedLanguages.dto.interace.ts";

export const fetchProjects = async (): Promise<IProjectDto[]> => {
  try {
    const response = await http.get("/api/v1/projects/lite/list");

    const projects = response.data;
    return projects as IProjectDto[];
  } catch (err) {
    console.error("Error fetching projects:", err);
    throw err;
  }
};

export const createNewProject = async (newProject: INewProject): Promise<IProjectDto> => {
  try {
    const response = await http.post("/api/v1/projects/lite/create", newProject);
    return response.data as IProjectDto
  } catch (err) {
    console.error("Error creating new project:", err);
    throw err;
  }
};

export const getProjectStatus = async (projectId: string): Promise<IProjectStatus> => {
  try {
    const response = await http.get(`/api/v1/status/lite/${projectId}`);
    return response.data as IProjectStatus
  } catch (err) {
    console.error("Error getting translations", err);
    throw err;
  }
};

export const getUploadLink = async (mimeType: SUPPORTED_FILE_TYPES, numVideos?: number): Promise<IUploadLink[]> => {
  try {
    let params: string = `mimeType=${mimeType}`
    if (numVideos) {
      params += `&numVideos=${numVideos}`
    }
    const response = await http.post(`/api/v1/projects/lite/upload_link?${params}`);
    return response.data as IUploadLink[];

  } catch (err) {
    console.error("Error getting upload link", err);
    throw err;
  }
};

export const sendFeedback = async (projectId: string, targetLang: string, feedbackForm: IFeedbackForm): Promise<void> => {
  try {
    await http.post(` / api / v1 / feedback / ${projectId}/languages/${targetLang}`
      , feedbackForm)
  } catch (err) {
    console.error("Error sending feedback", err);
    throw err;
  }
}

export const deleteOneProject = async (projectId: string): Promise<void> => {
  try {
    await http.delete(
      `/api/v1/projects/${projectId}`
    );
  } catch (err) {
    console.log("Error deleting")
    throw err;
  }
};

export const downloadTranslation = async (
  projectId: string,
  projectName: string,
): Promise<void> => {
  try {
    const response = await http.get(`/api/v1/projects/lite/${projectId}/download?language=all`, {responseType: "blob"});

    const blob = new Blob([response.data]); // ✅ Create blob from response
    const url = window.URL.createObjectURL(blob); // ✅ Create a temporary URL
    const a = document.createElement("a");
    const filename = `${projectName}.${mime.extension(response.headers["content-type"])}`;
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Error downloading translation", err);
    throw err;
  }
};

export const bulkDownload = async (
  projectIds: string[],
): Promise<void> => {
  try {
    const queryString = projectIds.map(id => `id=${id}`).join("&");

    const response = await http.get(`/api/v1/projects/lite/bulk-download?${queryString}`, {responseType: "blob"});

    const blob = new Blob([response.data]); // ✅ Create blob from response
    const url = window.URL.createObjectURL(blob); // ✅ Create a temporary URL
    const a = document.createElement("a");
    a.href = url;
    a.download = `bulk_projects.${mime.extension(response.headers["content-type"])}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Error downloading translation", err);
    throw err;
  }
};

export const getProjectStreamLinks = async (projectId: string): Promise<IProjectStreamLinks> => {
  const response = await http.get(`/api/v1/projects/lite/${projectId}/stream-links`);
  return response.data as IProjectStreamLinks
}

export const downloadFeatures = async (
  projectId: string,
  projectName: string,
  info: {
    subtitles?: string[],
    audioDescriptions?: string[],
    dialogueList?: boolean,
    extendedMetadata?: boolean,
  }
): Promise<void> => {
  try {
    const response = await http.post(`/api/v1/projects/lite/${projectId}/export-feature`, info, {responseType: "blob"});

    const blob = new Blob([response.data]); // ✅ Create blob from response
    const url: string = window.URL.createObjectURL(blob); // ✅ Create a temporary URL
    const a: HTMLAnchorElement = document.createElement("a");
    a.href = url;
    a.download = `${projectName}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Error downloading features", err);
    throw err;
  }
}

export const resetFeature = async (projectId: string, feature: string, languageCode: string | undefined): Promise<IProjectDto> => {
  try {
    const response = await http.put(`/api/v1/status/lite/${projectId}/reset-feature?feature=${feature}${languageCode ? `&language=${languageCode}` : ""}`);
    return response.data as IProjectDto
  } catch (err) {
    console.error("Error resetting feature", err);
    throw err;
  }
};